import * as React from "react";
import {
  List, Datagrid, FunctionField, TextInput,
  useRecordContext, useNotify, useRefresh,
} from "react-admin";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, TextField as MuiTextField, CircularProgress,
} from "@mui/material";
import { EnumChip } from "../../components/EnumChip";
import { ConfirmActionButton } from "../../components/ConfirmActionButton";
import { InfoGrid } from "../../components/InfoGrid";
import { PAYMENT_STATUS, BOOKING_STATUS, labelOf } from "../../enums";
import { Typography } from "@mui/material";
import { PersonChip, ShowLink } from "../../components/ui";
import { FrDateField } from "../../components/FrDateField";
import { fmtCents as fmtCentsFr, fmtDateTime } from "../../utils/format";

import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";
import { ProReferenceInput } from "../../components/RelationAutocomplete";

const Amount = ({ cents, strong }) => (
  <Typography component="span" variant="body2" sx={{ fontWeight: strong ? 600 : 400, whiteSpace: "nowrap", color: cents ? "text.primary" : "text.disabled" }}>
    {cents == null ? "—" : fmtCentsFr(cents)}
  </Typography>
);

// Payment entity (backend) : id, paymentIntent, paymentStatusEnum, amount (long, CENTIMES),
// bookingId, customerId, description, idempotencyKey, proId, createdAt (Long ms), paidAt,
// nextPayoutDate, stripeAccountId, transferAmount, transferId, payoutId.
// PAS de patientId direct — le patient se retrouve via le booking.
//
// Backend : GET /payment (liste paginée, filtres proId | bookingId
// uniquement). Il n'existe PAS de GET /payment/{id} : pas de fiche, le
// détail s'affiche en dépliant la ligne (données de la liste).

const filters = [
  <ProReferenceInput key="proId" source="proId" label="Professionnel" alwaysOn />,
  <TextInput key="bookingId" source="bookingId" label="Identifiant du RDV" />,
];

const fmtCents = (cents) => fmtCentsFr(cents);

// POST /payment/refund/{paymentIntentId}?amount=<centimes> (admin) :
// le montant est obligatoire → saisi en euros, envoyé en centimes.
const RefundButton = () => {
  const r = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = React.useState(false);
  const [amount, setAmount] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  if (!r?.paymentIntent) return null;
  const openDialog = () => {
    setAmount(r.amount != null ? (Number(r.amount) / 100).toFixed(2) : "");
    setOpen(true);
  };
  const submit = async () => {
    const cents = Math.round(Number(String(amount).replace(",", ".")) * 100);
    if (!cents || cents < 1) return;
    setLoading(true);
    try {
      await httpClient(
        `${API_BASE_URL}/payment/refund/${encodeURIComponent(r.paymentIntent)}?amount=${cents}`,
        { method: "POST" }
      );
      notify(`Remboursement de ${fmtCents(cents)} initié`, { type: "success" });
      setOpen(false);
      refresh();
    } catch (e) {
      notify(`Remboursement impossible : ${e.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Button size="small" variant="contained" color="info" onClick={openDialog}>
        Rembourser
      </Button>
      <Dialog open={open} onClose={() => !loading && setOpen(false)}>
        <DialogTitle>Rembourser cette transaction</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            PaymentIntent {r.paymentIntent} — montant initial {fmtCents(r.amount)}.
          </DialogContentText>
          <MuiTextField
            label="Montant à rembourser (€)"
            type="number"
            size="small"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0.01, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>Fermer</Button>
          <Button onClick={submit} color="info" variant="contained"
            disabled={loading || !(Number(amount) > 0)}>
            {loading ? <CircularProgress size={20} /> : "Rembourser"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CancelPI = () => {
  const r = useRecordContext();
  if (!r?.paymentIntent) return null;
  const pid = r.paymentIntent;
  return (
    <ConfirmActionButton label="Annuler le PaymentIntent" color="error"
      confirmTitle="Annuler" confirmText="Annuler ce PaymentIntent Stripe ?"
      successMessage="PI annulé"
      onConfirm={() => httpClient(`${API_BASE_URL}/payment/cancel/${pid}`, { method: "POST" })}
    />
  );
};

const VerifyPI = () => {
  const r = useRecordContext();
  const [info, setInfo] = React.useState(null);
  if (!r?.paymentIntent) return null;
  const pid = r.paymentIntent;
  const check = async () => {
    try {
      // Le backend renvoie le statut en texte brut (ex. "succeeded")
      const { json, body } = await httpClient(`${API_BASE_URL}/payment/status/${pid}`);
      const text = (typeof json === "string" && json) || body || "";
      setInfo({ status: String(text).trim().replace(/^"|"$/g, "") || "(vide)" });
    } catch (e) {
      setInfo({ error: e.message });
    }
  };
  return (
    <Box sx={{ mt: 1 }}>
      <button onClick={check} style={{
        padding: "6px 12px", borderRadius: 6, border: "1px solid #0d7377",
        background: "white", color: "#0d7377", cursor: "pointer",
      }}>
        Vérifier le statut Stripe
      </button>
      {info && (
        <Box component="pre" sx={{ fontSize: 12, mt: 1, p: 1, bgcolor: "#f5f7fa", borderRadius: 1 }}>
          {info.error ? `Erreur : ${info.error}` : `Statut Stripe : ${info.status}`}
        </Box>
      )}
    </Box>
  );
};

// Détail d'une transaction : données de la ligne (pas de GET /payment/{id}).
const PaymentDetails = () => (
  <Box sx={{ p: 1 }}>
    <InfoGrid
      fields={[
        { label: "ID", source: "id", type: "mono" },
        { label: "Stripe PaymentIntent", source: "paymentIntent", type: "mono" },
        { label: "Statut", source: (r) => labelOf(PAYMENT_STATUS, r?.paymentStatusEnum) },
        { label: "Montant", source: (r) => fmtCents(r?.amount) },
        { label: "Créé le", source: "createdAt", type: "datetime" },
        { label: "Payé le", source: "paidAt", type: "datetime" },
        { label: "Prochain payout", source: "nextPayoutDate", type: "datetime" },
        { label: "Pro ID", source: "proId", type: "mono" },
        { label: "Booking ID", source: "bookingId", type: "mono" },
        { label: "Customer Stripe", source: "customerId", type: "mono" },
        { label: "Description", source: "description" },
        { label: "Patient", source: "patientName" },
        { label: "Professionnel", source: "proName" },
        { label: "Empreinte", source: (r) => fmtCents(r?.authorizedAmount) },
        { label: "Capturé", source: (r) => fmtCents(r?.capturedAmount) },
        { label: "Remboursé", source: (r) => fmtCents(r?.refundedAmount) },
        { label: "Frais plateforme", source: (r) => fmtCents(r?.platformFee) },
        { label: "Net praticien", source: (r) => fmtCents(r?.ownerNet) },
        { label: "Origine", source: (r) => (r?.source === "BOOKING" ? "Rendez-vous" : r?.source) },
        { label: "Statut du RDV", source: (r) => labelOf(BOOKING_STATUS, r?.bookingStatus) },
        { label: "Stripe Account ID", source: "stripeAccountId", type: "mono" },
        { label: "Montant transféré au pro", source: (r) => fmtCents(r?.transferAmount) },
        { label: "Transfer ID", source: "transferId", type: "mono" },
        { label: "Payout ID", source: "payoutId", type: "mono" },
      ]}
    />
    <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
      <RefundButton /><CancelPI />
    </Box>
    <VerifyPI />
  </Box>
);

export default {
  list: () => (
    <List filters={filters} perPage={25}>
      <Datagrid rowClick="expand" expand={<PaymentDetails />} bulkActionButtons={false}>
        <FrDateField source="createdAt" label="Date" showTime />
        <FunctionField label="Patient" render={(r) => <PersonChip id={r?.patientId} name={r?.patientName} resource="patients" dense />} />
        <FunctionField label="Professionnel" render={(r) => <PersonChip id={r?.proId} name={r?.proName} resource="professionals" dense />} />
        <FunctionField label="Montant" textAlign="right" render={(r) => <Amount cents={r?.amount ?? r?.authorizedAmount} strong />} />
        <FunctionField label="Capturé" textAlign="right" render={(r) => <Amount cents={r?.capturedAmount} />} />
        <FunctionField label="Remboursé" textAlign="right" render={(r) => <Amount cents={r?.refundedAmount} />} />
        <EnumChip source="paymentStatusEnum" enumObj={PAYMENT_STATUS} label="Statut" />
        <FunctionField label="Rendez-vous" render={(r) => r?.bookingId ? (
          <ShowLink resource="bookings" id={r.bookingId}>{r?.slotStartTime ? fmtDateTime(r.slotStartTime) : "Voir le RDV"}</ShowLink>
        ) : "—"} />
      </Datagrid>
    </List>
  ),
};
